/* Native modal and Pointer Events; no libraries, cart calls or analytics. */
const elementName = 'spotmystuff-scratch-popup';
const storageKey = 'spotmystuff:scratch:v1';
const cookieKey = 'sms_scratch_v1';
let owner = null;
let memoryRecord = null;

function readRecord() {
  const records = [memoryRecord];
  for (const kind of ['localStorage', 'sessionStorage']) {
    try { records.push(JSON.parse(window[kind].getItem(storageKey))); } catch { /* Private mode. */ }
  }
  try {
    const cookie = document.cookie.split('; ').find((part) => part.startsWith(`${cookieKey}=`));
    if (cookie) records.push(JSON.parse(decodeURIComponent(cookie.slice(cookieKey.length + 1))));
  } catch { /* Cookies can also be blocked. */ }
  return records.filter((record) => record && Number.isFinite(record.at))
    .sort((a, b) => b.at - a.at)[0];
}

function remember(status, days) {
  memoryRecord = { at: Date.now(), status };
  const value = JSON.stringify(memoryRecord);
  let saved = false;
  for (const kind of ['localStorage', 'sessionStorage']) {
    try { window[kind].setItem(storageKey, value); if (kind === 'localStorage') saved = true; } catch { /* Try next store. */ }
  }
  if (!saved) {
    try { document.cookie = `${cookieKey}=${encodeURIComponent(value)}; Max-Age=${days * 86400}; Path=/; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`; } catch { /* In-memory fallback remains. */ }
  }
}

class ScratchPopup extends HTMLElement {
  connectedCallback() {
    if (this.connected || window.Shopify?.designMode || (owner && owner !== this)) return;
    this.dialog = this.querySelector('dialog');
    if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
    owner = this;
    this.connected = true;
    this.days = Math.min(30, Math.max(1, Number(this.dataset.repeatDays) || 7));
    this.threshold = Math.min(90, Math.max(10, Number(this.dataset.threshold) || 40));
    this.canvas = this.querySelector('canvas');
    this.abort = new AbortController();
    const on = (target, event, handler, options = {}) => target.addEventListener(event, handler, { ...options, signal: this.abort.signal });
    this.on = on;
    on(this, 'click', (event) => {
      const button = event.target.closest('button');
      if (!button) return;
      if (button.hasAttribute('data-close')) {
        if (!button.hidden) this.finish(this.completed ? 'completed' : 'dismissed');
        return;
      }
      if (this.transitioning) return;
      if (button.hasAttribute('data-claim') && this.revealed && !button.hidden) {
        this.completed = true;
        remember('completed', this.days);
        this.screen('success');
      }
      if (button.hasAttribute('data-continue')) this.finish('completed');
    });
    on(this.dialog, 'cancel', (event) => { event.preventDefault(); this.finish(this.completed ? 'completed' : 'dismissed'); });
    on(this.dialog, 'close', () => { if (this.lock) this.finish(this.completed ? 'completed' : 'dismissed'); });
    on(this.dialog, 'keydown', (event) => {
      if (event.key !== 'Tab') return;
      const buttons = [...this.dialog.querySelectorAll('button')].filter((button) => button.getClientRects().length && !button.disabled);
      const first = buttons[0], last = buttons[buttons.length - 1];
      if (event.shiftKey && (document.activeElement === first || !buttons.includes(document.activeElement))) {
        event.preventDefault(); last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    });
    on(document, 'pointerdown', () => { this.lastInteraction = Date.now(); }, { passive: true });
    on(document, 'keydown', () => { this.lastInteraction = Date.now(); });
    on(document, 'theme-drawer:open', () => {
      // A pending cart response can open a drawer after our delay has elapsed.
      if (this.dialog.open) this.finish('dismissed', false);
    });
    on(window, 'pagehide', () => { if (this.dialog.open) this.finish(this.completed ? 'completed' : 'dismissed'); });
    on(this.canvas, 'pointerdown', (event) => this.startStroke(event));
    on(this.canvas, 'pointermove', (event) => this.moveStroke(event));
    for (const event of ['pointerup', 'pointercancel', 'lostpointercapture']) on(this.canvas, event, () => {
      this.pointer = null; this.lastPoint = null; this.checkProgress();
    });
    this.timer = window.setTimeout(() => this.tryOpen(), Math.max(0, Number(this.dataset.delay) || 0) * 1000);
  }

  suppressed() {
    const record = readRecord();
    return record && Date.now() - record.at < this.days * 86400000;
  }

  tryOpen() {
    if (!this.isConnected || this.suppressed()) return;
    const otherModal = [...document.querySelectorAll('dialog[open], [aria-modal="true"], [role="dialog"]')]
      .some((node) => node !== this.dialog && node.getClientRects().length && getComputedStyle(node).visibility !== 'hidden');
    const focusedInput = document.activeElement?.matches('input, select, textarea, [contenteditable="true"]');
    // Horizon 4.1.1 deliberately hides root overflow on desktop; its page-wrapper scrolls.
    const wrapper = document.querySelector('.page-wrapper');
    const scrollRoot = window.matchMedia('(min-width: 990px)').matches && wrapper ? wrapper : document.documentElement;
    const lockedPage = document.documentElement.hasAttribute('scroll-lock') || /hidden|clip/.test(getComputedStyle(scrollRoot).overflowY) || getComputedStyle(document.body).position === 'fixed';
    if (document.hidden || otherModal || focusedInput || lockedPage || Date.now() - (this.lastInteraction || 0) < 1500) {
      this.timer = window.setTimeout(() => this.tryOpen(), 1000);
      return;
    }
    this.previousFocus = document.activeElement;
    this.lockScroll();
    try { this.dialog.showModal(); } catch { this.unlockScroll(); return; }
    remember('shown', this.days); // Navigation/reload must not immediately trigger another popup.
    this.dialog.querySelector('h2').focus({ preventScroll: true });
    this.resizeCanvas();
    if ('ResizeObserver' in window) {
      this.observer = new ResizeObserver(() => this.resizeCanvas());
      this.observer.observe(this.canvas.parentElement);
    }
    this.on(window, 'resize', () => this.resizeCanvas(), { passive: true });
  }

  lockScroll() {
    const body = document.body;
    const wrapper = document.querySelector('.page-wrapper');
    const desktop = window.matchMedia('(min-width: 990px)').matches && wrapper;
    const values = { position: 'fixed', top: `${-window.scrollY}px`, left: `${-window.scrollX}px`, width: '100%', overflow: 'hidden' };
    this.lock = { x: desktop ? wrapper.scrollLeft : window.scrollX, y: desktop ? wrapper.scrollTop : window.scrollY, wrapper, properties: [] };
    const lockProperties = (node, properties) => {
      for (const [name, value] of Object.entries(properties)) {
        this.lock.properties.push({ node, name, value: node.style.getPropertyValue(name), priority: node.style.getPropertyPriority(name), applied: value });
        node.style.setProperty(name, value, 'important');
      }
    };
    lockProperties(body, values);
    if (wrapper) {
      lockProperties(wrapper, { 'overflow-y': 'hidden' });
    }
  }

  unlockScroll() {
    if (!this.lock) return;
    const lock = this.lock;
    this.lock = null;
    for (const item of lock.properties) {
      // Do not overwrite an unrelated component's newer inline change.
      if (item.node.style.getPropertyValue(item.name) !== item.applied) continue;
      if (item.value) item.node.style.setProperty(item.name, item.value, item.priority);
      else item.node.style.removeProperty(item.name);
    }
    const scroller = window.matchMedia('(min-width: 990px)').matches && lock.wrapper ? lock.wrapper : window;
    scroller.scrollTo({ left: lock.x, top: lock.y, behavior: 'instant' });
  }

  resizeCanvas() {
    if (this.revealed || !this.dialog.open) return;
    const rect = this.canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    const width = Math.round(rect.width * dpr), height = Math.round(rect.height * dpr);
    if (this.canvas.width === width && this.canvas.height === height && this.context) return;
    const previous = document.createElement('canvas');
    if (this.context) {
      previous.width = this.canvas.width; previous.height = this.canvas.height;
      previous.getContext('2d').drawImage(this.canvas, 0, 0);
    }
    this.canvas.width = width; this.canvas.height = height;
    this.context = this.canvas.getContext('2d', { willReadFrequently: true });
    if (!this.context) { this.showClose(); return; }
    const context = this.context;
    context.setTransform(dpr, 0, 0, dpr, 0, 0);
    if (previous.width && this.initializedCanvas) context.drawImage(previous, 0, 0, rect.width, rect.height);
    else {
      context.fillStyle = '#020405'; context.fillRect(0, 0, rect.width, rect.height);
      context.fillStyle = '#fff'; context.font = `600 ${Math.max(20, rect.width * .07)}px Arial, sans-serif`;
      context.textAlign = 'center'; context.textBaseline = 'middle';
      context.fillText('Scratch Off', rect.width / 2, rect.height / 2);
    }
    this.initializedCanvas = true;
    context.globalCompositeOperation = 'destination-out';
    context.lineWidth = Math.max(34, rect.width * .13);
    context.lineCap = 'round'; context.lineJoin = 'round';
    this.pointer = null; this.lastPoint = null;
  }

  point(event) {
    const rect = this.canvas.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  startStroke(event) {
    if (this.revealed || !this.context || event.isPrimary === false || (event.pointerType === 'mouse' && event.button !== 0)) return;
    event.preventDefault();
    this.pointer = event.pointerId;
    this.canvas.setPointerCapture(event.pointerId);
    this.lastPoint = this.point(event);
    this.erase(this.lastPoint);
    this.showClose();
  }

  moveStroke(event) {
    if (this.pointer !== event.pointerId || this.revealed) return;
    event.preventDefault();
    this.erase(this.point(event));
    if (performance.now() - (this.lastCheck || 0) > 120) this.checkProgress();
  }

  erase(point) {
    const context = this.context;
    context.beginPath();
    context.moveTo(this.lastPoint.x, this.lastPoint.y);
    context.lineTo(point.x, point.y);
    context.stroke();
    context.beginPath(); context.arc(point.x, point.y, context.lineWidth / 2, 0, Math.PI * 2); context.fill();
    this.lastPoint = point;
  }

  checkProgress() {
    if (!this.context || this.revealed) return;
    this.lastCheck = performance.now();
    try {
      const { data } = this.context.getImageData(0, 0, this.canvas.width, this.canvas.height);
      let cleared = 0, total = 0;
      // Regular pixel sampling measures erased area, not the length of repeated strokes.
      for (let y = 4; y < this.canvas.height; y += 8) {
        for (let x = 4; x < this.canvas.width; x += 8) {
          total++; if (data[(y * this.canvas.width + x) * 4 + 3] < 128) cleared++;
        }
      }
      if (total && cleared / total * 100 >= this.threshold) this.reveal();
    } catch { /* Keep the popup dismissible if pixel reading is unavailable. */ this.showClose(); }
  }

  reveal() {
    if (this.revealed) return;
    this.revealed = true; this.pointer = null;
    this.showClose();
    this.observer?.disconnect();
    return this.transitionContent(() => {
      this.canvas.hidden = true;
      this.querySelector('[data-claim]').hidden = false;
      const reward = this.querySelector('[data-reward]');
      reward.removeAttribute('aria-hidden');
      reward.focus({ preventScroll: true });
    });
  }

  showClose() {
    this.querySelector('[data-close]').hidden = false;
  }

  screen(name) {
    return this.transitionContent(() => {
      this.querySelectorAll('[data-screen]').forEach((screen) => { screen.hidden = screen.dataset.screen !== name; });
      const heading = this.querySelector(`[data-screen="${name}"] h2`);
      this.dialog.setAttribute('aria-labelledby', heading.id);
      this.dialog.scrollTop = 0;
      heading.focus({ preventScroll: true });
    });
  }

  async transitionContent(update) {
    if (this.transitioning || !this.dialog.open) return;
    const content = this.querySelector('.sms-scratch__content');
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || typeof content.animate !== 'function') {
      update();
      return;
    }
    this.transitioning = true;
    content.setAttribute('data-transitioning', '');
    try {
      this.transitionAnimation = content.animate(
        [{ opacity: 1, transform: 'translateY(0)' }, { opacity: 0, transform: 'translateY(-6px)' }],
        { duration: 130, easing: 'ease-in', fill: 'forwards' }
      );
      await this.transitionAnimation.finished;
      if (!this.dialog.open || !this.isConnected) return;
      // Switch layout while invisible so different card heights do not visibly jump.
      update();
      this.transitionAnimation.cancel();
      this.transitionAnimation = content.animate(
        [{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'translateY(0)' }],
        { duration: 230, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'forwards' }
      );
      await this.transitionAnimation.finished;
    } catch {
      // Closing or removing the section cancels pending animations intentionally.
    } finally {
      this.transitionAnimation?.cancel();
      this.transitionAnimation = null;
      content.removeAttribute('data-transitioning');
      this.transitioning = false;
    }
  }

  finish(status, restoreFocus = true) {
    clearTimeout(this.timer);
    this.transitionAnimation?.cancel();
    remember(status, this.days);
    this.observer?.disconnect();
    this.unlockScroll();
    if (this.dialog.open) this.dialog.close();
    if (restoreFocus && this.previousFocus?.isConnected) this.previousFocus.focus({ preventScroll: true });
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.transitionAnimation?.cancel();
    this.observer?.disconnect();
    this.abort?.abort();
    if (this.dialog?.open) this.finish(this.completed ? 'completed' : 'dismissed');
    this.unlockScroll();
    this.connected = false;
    if (owner === this) owner = null;
  }
}

if (!customElements.get(elementName)) customElements.define(elementName, ScratchPopup);
